import React, { useState, useEffect } from "react";
import { getFollowUpById, updateFormFollowUp } from "../../apiServices";
import { Button, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { decryptedAdminID } from "../../utility";
import { AiOutlineClose } from "react-icons/ai";

const UpdateFollowUp = ({ followUpId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    id: followUpId,
    title: "",
    description: "",
    followup_date: "",
    priority: "",
    status: "",
    // reason: ""
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    title: "",
    priority: "",
    status: "",
    followup_date: ""
  });
  const [touched, setTouched] = useState({
    title: false,
    priority: false,
    status: false,
    followup_date: false
  });
  const [submitted, setSubmitted] = useState(false);
  const adminID = Number(decryptedAdminID());

  // Priority options
  const priorityOptions = [
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "CRITICAL", label: "Critical" },
  ];

  // Status options
  const statusOptions = [
    { value: "PENDING", label: "Pending" },
    { value: "COMPLETED", label: "Completed" },
    { value: "TODO", label: "TODO" },
    // { value: "RESCHEDULED", label: "Rescheduled" },
  ];

  // Title options
  const titleOptions = [
    { value: "Call Remainder", label: "Call Remainder" },
    { value: "Interview Follow Up", label: "Interview Follow Up" },
    { value: "Joining Follow Up", label: "Joining Follow Up" },
  ];

  // Check if followup_date should be disabled based on title
  const isFollowupDateDisabled = () => {
    return formData.title === "Interview Follow Up" || formData.title === "Joining Follow Up";
  };

  // Fetch existing follow-up details
  useEffect(() => {
    const fetchFollowUpDetails = async () => {
      if (!followUpId) {
        setError("No follow-up ID provided");
        setFetchLoading(false);
        return;
      }

      try {
        setFetchLoading(true);
        console.log("Fetching follow-up with ID:", followUpId);

        const response = await getFollowUpById(followUpId);
        console.log("Processed API Response:", response);

        if (!response) {
          throw new Error("No data received from server");
        }

        // Format date from API format to date input format
        let formattedDate = "";
        if (response.followup_date) {
          // Extract just the date part (YYYY-MM-DD) from the API response
          formattedDate = response.followup_date.split(' ')[0];
        }

        // Handle empty values - if field is empty/null/undefined, set to empty string
        setFormData({
          id: followUpId,
          title: response.title || "",
          description: response.description || "",
          followup_date: formattedDate || "",
          priority: response.priority || "",
          status: response.status || "",
          // reason: response.closed_reason || response.reason || ""
        });

        setError("");
      } catch (err) {
        console.error("Error fetching follow-up:", err);
        let errorMessage = "Failed to fetch follow-up details. ";
        if (err.message.includes("<!DOCTYPE")) {
          errorMessage += "The server returned an HTML error page. Please check if the API endpoint is correct.";
        } else if (err.message.includes("HTTP error")) {
          errorMessage += err.message;
        } else {
          errorMessage += err.message;
        }
        setError(errorMessage);
      } finally {
        setFetchLoading(false);
      }
    };

    fetchFollowUpDetails();
  }, [followUpId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Don't allow changes to followup_date if it's disabled
    if (name === "followup_date" && isFollowupDateDisabled()) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing/selecting
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (error) setError("");

    // If form was submitted before, re-validate on change
    if (submitted) {
      validateSingleField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true
    }));

    // Validate on blur
    validateSingleField(name, value);
  };

  const validateSingleField = (fieldName, value) => {
    let errorMessage = "";

    switch (fieldName) {
      case "title":
        if (!value || value.trim() === "") {
          errorMessage = "Please select a title";
        }
        break;
      case "priority":
        if (!value || value.trim() === "") {
          errorMessage = "Please select priority";
        }
        break;
      case "status":
        if (!value || value.trim() === "") {
          errorMessage = "Please select status";
        }
        break;
      case "followup_date":
        // Only validate followup_date if it's not disabled
        if (!isFollowupDateDisabled()) {
          if (!value || value.trim() === "") {
            errorMessage = "Please select follow-up date";
          }
        }
        break;
      default:
        break;
    }

    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: errorMessage
    }));

    return errorMessage === "";
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate title
    if (!formData.title || formData.title.trim() === "") {
      errors.title = "Please select a title";
      isValid = false;
    }

    // Validate priority
    if (!formData.priority || formData.priority.trim() === "") {
      errors.priority = "Please select priority";
      isValid = false;
    }

    // Validate status
    if (!formData.status || formData.status.trim() === "") {
      errors.status = "Please select status";
      isValid = false;
    }

    // Validate followup_date only if not disabled
    if (!isFollowupDateDisabled()) {
      if (!formData.followup_date || formData.followup_date.trim() === "") {
        errors.followup_date = "Please select follow-up date";
        isValid = false;
      }
    }

    setFieldErrors(errors);

    return isValid;
  };

  const formatDateTimeForAPI = (date) => {
    if (!date) return null;
    // Add '00:00:00' as the time portion
    return date + ' 00:00:00';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark as submitted
    setSubmitted(true);

    // Mark all fields as touched
    setTouched({
      title: true,
      priority: true,
      status: true,
      followup_date: true
    });

    // Validate form
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.is-invalid');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      id: formData.id,
      admin_id: decryptedAdminID()
    };

    if (formData.title && formData.title.trim() !== "") {
      payload.title = formData.title;
    }

    if (formData.description && formData.description.trim() !== "") {
      payload.description = formData.description;
    }

    // Only include followup_date in payload if it's not disabled
    if (!isFollowupDateDisabled() && formData.followup_date) {
      payload.followup_date = formatDateTimeForAPI(formData.followup_date);
    }

    if (formData.priority && formData.priority !== "") {
      payload.priority = formData.priority;
    }

    if (formData.status && formData.status !== "") {
      payload.status = formData.status;
    }

    // if (formData.reason && formData.reason.trim() !== "") {
    //   payload.reason = formData.reason;
    // }

    try {
      const result = await updateFormFollowUp(payload);
      console.log("Follow-up updated successfully:", result);

      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error("Error updating follow-up:", err);
      let errorMessage = "Failed to update follow-up. ";
      if (err.message.includes("<!DOCTYPE")) {
        errorMessage += "The server returned an HTML error page. Please check if the API endpoint is correct.";
      } else {
        errorMessage += err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading follow-up details...</p>
      </div>
    );
  }

  return (
    <div style={{minWidth:"400px"}}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h6 className="mb-0 ">Update Follow-up</h6>
        <button className="btn btn-outline-danger" onClick={onCancel}>
          <AiOutlineClose />
        </button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Form onSubmit={handleSubmit} noValidate>
        {/* Row 1: Title and Priority */}
        <Row className="mb-3">
          <Col md="6">
            <Form.Group controlId="title">
              <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Title</Form.Label>
              <Form.Control
                as="select"
                name="title"
                value={formData.title}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled  // Title is always disabled, only view
                isInvalid={(touched.title || submitted) && fieldErrors.title}
                style={{ fontSize: '13px', padding: '6px 10px' }}
              >
                <option value="" disabled>Select Title</option>
                {titleOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {fieldErrors.title}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="followup_date">
              <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>
                Follow-up Date
                {!isFollowupDateDisabled() && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                type="date"
                name="followup_date"
                value={formData.followup_date}
                onChange={handleChange}
                onBlur={handleBlur}
                min={new Date().toISOString().split('T')[0]} 
                disabled={isFollowupDateDisabled()}  // Disabled when title is Interview/Joining Follow Up
                isInvalid={(touched.followup_date || submitted) && fieldErrors.followup_date}
                style={{
                  fontSize: '13px',
                  padding: '6px 10px',
                  backgroundColor: isFollowupDateDisabled() ? '#e9ecef' : 'white'
                }}
              />
              {/* {isFollowupDateDisabled() && (
                <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                  Follow-up date is not editable for this title
                </Form.Text>
              )} */}
              <Form.Control.Feedback type="invalid">
                {fieldErrors.followup_date}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

        </Row>

        {/* Row 2: Status and Follow-up Date */}
        <Row className="mb-3">
          <Col md="6">
            <Form.Group controlId="status">
              <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Status <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="select"
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled
                isInvalid={(touched.status || submitted) && fieldErrors.status}
                style={{ fontSize: '13px', padding: '6px 10px' }}
              >
                <option value="" disabled>Select Status</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {fieldErrors.status}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group controlId="priority">
              <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Priority <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="select"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={(touched.priority || submitted) && fieldErrors.priority}
                style={{ fontSize: '13px', padding: '6px 10px' }}
              >
                <option value="" disabled>Select Priority</option>
                {priorityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {fieldErrors.priority}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

        </Row>

        {/* Row 3: Description and Reason - No validation errors */}
        <Row className="mb-3">
          <Col md="12">
            <Form.Group controlId="description">
              <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter details..."
                rows="2"
                disabled={formData.title === "Interview Follow Up" || formData.title === "Joining Follow Up"} // Disable description for Interview/Joining Follow Up
                style={{ fontSize: '13px', padding: '6px 10px' }}
              />
            </Form.Group>
          </Col>

          {/* <Col md="6">
            <Form.Group controlId="reason">
              <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Reason</Form.Label>
              <Form.Control
                as="textarea"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Enter reason for update or cancellation..."
                rows="4"
                style={{ fontSize: '13px', padding: '6px 10px' }}
              />
            </Form.Group>
          </Col> */}
        </Row>

        {/* Button Group */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button variant="danger" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Updating...</span>
              </>
            ) : (
              "Update Follow-up"
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UpdateFollowUp;